import { motion } from "framer-motion";
import { GraduationCap, BadgeCheck } from "lucide-react";
import { PipitoneButton } from "@/components/decor";

const FOTO_URL = "https://pipitone.ch/foto-pipitone.jpg";

const BIO_PARAGRAPHS = [
  "Laureato in Medicina e Chirurgia in Italia (Università Campus Bio-Medico di Roma e Università degli Studi di Palermo), ho proseguito la mia formazione in Svizzera presso l'Organizzazione Socio-Psichiatrica Cantonale (OSC), dove ho conseguito il titolo di specialista FMH in Psichiatria e Psicoterapia.",
  "Ho maturato esperienza professionale presso la Clinica Psichiatrica Cantonale di Mendrisio, dove ho anche ricoperto il ruolo di medico sostituto Capoclinica nel reparto dedicato agli esordi psicotici, e nei servizi territoriali dell'OSC, in particolare nell'Home Treatment — servizio di ricovero domiciliare di psichiatria acuta — e nel PATH-Team (Psichiatria Assertiva Territoriale e Housing), équipe specializzata nella presa a carico intensiva di persone affette da disturbi psicotici e affettivi, sviluppando competenza nel trattamento integrato del malessere psichico, sia in ambito stazionario, che ambulatoriale e domiciliare.",
  "Ho inoltre completato un anno di formazione in Neurologia presso la Clinica Hildebrand di Brissago, approfondendo le interconnessioni tra disturbi neurologici e psichiatrici. Parallelamente alla formazione medico-psichiatrica, ho conseguito il titolo di Psicoterapeuta a orientamento junghiano presso il Laboratorio di Formazione Analitica della Svizzera Italiana, antenna del C. G. Jung-Institut Zürich, e attualmente proseguo il mio percorso di approfondimento come Psicologo Analista presso il Centro Italiano di Psicologia Analitica (CIPA), affiliato alla IAAP (International Association for Analytical Psychology).",
  "I miei ambiti di interesse comprendono in particolare i disturbi d'ansia, con le loro diverse manifestazioni (attacchi di panico, fobie, ansia generalizzata), i disturbi ossessivo-compulsivi, i disturbi depressivi, i disturbi psicosomatici e somatoformi, in cui la sofferenza psichica si esprime attraverso il corpo, e il disturbo da deficit di attenzione/iperattività (ADHD) e i disturbi dello spettro autistico dell'adulto.",
  "Accanto alla valutazione e al trattamento dei disturbi psichiatrici, offro percorsi di psicoterapia a orientamento psicoanalitico, con particolare riferimento alla psicologia analitica di Carl Gustav Jung. Il lavoro analitico non si rivolge soltanto al sintomo, ma alla persona nella sua interezza e alla comprensione dei processi, anche inconsci, che ne attraversano la vita psichica. Il percorso può così diventare uno spazio di esplorazione della propria storia, dei conflitti interiori, delle modalità relazionali, dei sogni e dei significati che emergono dall'esperienza, favorendo una più profonda conoscenza di sé.",
];

const CREDENZIALI = [
  "Medico Psichiatra FMH",
  "Psicoterapeuta a orientamento junghiano",
  "TFP Didactic Training Graduate (O. Kernberg)",
  "Psicologo analista in formazione CIPA",
  "Docente SUPSI",
];

const ChiSono = () => (
  <section id="chi-sono" className="relative overflow-hidden bg-[#F4EFEA] py-20 sm:py-28" data-testid="chi-sono-section">
    <div
      aria-hidden
      className="absolute -left-32 bottom-0 h-[420px] w-[420px] rounded-full opacity-50 blur-3xl"
      style={{ background: "radial-gradient(circle, rgba(13,148,136,0.12), transparent 65%)" }}
    />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
      <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12 lg:gap-16">
        <motion.figure
          initial={{ opacity: 0, y: 40, rotate: -4 }}
          whileInView={{ opacity: 1, y: 0, rotate: -2 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-72 sm:w-96 lg:col-span-5"
        >
          <div className="absolute -inset-5 rounded-[2rem] bg-[#0D9488]/10 blur-xl" aria-hidden />
          <img
            src={FOTO_URL}
            alt="Dr. med. Nicolò Pipitone, Psichiatra e Psicoterapeuta FMH"
            data-testid="chi-sono-foto"
            className="relative w-full rounded-2xl border border-[#E2D9CD] object-cover shadow-[0_30px_60px_rgba(12,27,51,0.22)]"
          />
          <div className="absolute -bottom-6 left-1/2 w-max -translate-x-1/2 rounded-full bg-[#0C1B33] px-5 py-2.5 text-center shadow-lg">
            <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-[#14B8A6]">
              Psichiatra e Psicoterapeuta
            </span>
            <span className="block text-xs font-semibold text-white">FMH · Lugano Paradiso</span>
          </div>
        </motion.figure>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.75, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.22em] text-[#0D9488]">02 — Chi sono</p>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-[#0C1B33] sm:text-5xl" data-testid="chi-sono-titolo">
            Dr. med. Nicolò Pipitone
          </h2>
          <p className="mt-3 font-display text-lg italic text-[#0D9488]">
            Psichiatra e Psicoterapeuta FMH
          </p>

          <div className="mt-6 space-y-4 text-sm leading-relaxed text-[#475569] sm:text-base" data-testid="chi-sono-bio">
            {BIO_PARAGRAPHS.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <div className="mt-8" data-testid="chi-sono-credenziali">
            <p className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#0C1B33]/70">
              <GraduationCap className="h-4 w-4 text-[#0D9488]" />
              Formazione e titoli
            </p>
            <ul className="flex flex-wrap gap-2.5">
              {CREDENZIALI.map((c) => (
                <li
                  key={c}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#E2D9CD] bg-[#FFFDF9] px-3.5 py-1.5 text-xs font-medium text-[#0C1B33]"
                >
                  <BadgeCheck className="h-3.5 w-3.5 text-[#0D9488]" />
                  {c}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-9">
            <PipitoneButton testId="chi-sono-pipitone-link" />
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

export default ChiSono;
